import { Component, Input, OnInit } from '@angular/core';
import { ICerere, CardWithForms, GroupWithItems, FormItem, SubGroup } from '../../cereri.interface';
import { CereriService } from '../../cereri.service';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
    selector: 'app-informatii-aditionale',
    templateUrl: './informatii-aditionale.component.html',
    styleUrls: ['./informatii-aditionale.component.scss'],
})
export class InformatiiAditionaleComponent implements OnInit {
    @Input() cerere!: ICerere;

    // Cached data
    extraInfoCards: CardWithForms[] = [];
    selectedCard: CardWithForms | null = null;
    selectedGroup: GroupWithItems | null = null;

    // Selection state
    selectedCardSlug: string = '';
    selectedGroupKey: string = '';

    // Computed properties for performance
    selectedCardGroupKeys: string[] = [];
    selectedGroupItems: FormItem[] = [];
    selectedGroupSubgroups: SubGroup[] = [];

    constructor(
        private cereriService: CereriService,
        public permissionsService: PermissionsService
    ) { }

    ngOnInit(): void {
        this.initializeData();
    }

    private initializeData(): void {
        this.extraInfoCards = this.getExtraInfo();

        // Auto-select first card if available
        if (this.extraInfoCards.length > 0) {
            this.selectCard(this.extraInfoCards[0].info.slug);
        }
    }

    private getExtraInfo(): CardWithForms[] {
        return this.cerere.extra_info ? Object.values(this.cerere.extra_info) : [];
    }

    selectCard(cardSlug: string): void {
        if (this.selectedCardSlug === cardSlug) return;

        this.selectedCardSlug = cardSlug;
        this.selectedCard = this.extraInfoCards.find(card => card.info.slug === cardSlug) || null;

        if (this.selectedCard) {
            this.selectedCardGroupKeys = this.getGroupKeys(cardSlug);

            // Auto-select first group if available
            if (this.selectedCardGroupKeys.length > 0) {
                this.selectGroup(this.selectedCardGroupKeys[0]);
            } else {
                this.clearGroupSelection();
            }
        }
    }

    selectGroup(groupKey: string): void {
        if (!this.selectedCard || groupKey === 'info') return;

        // Don't allow deselection if this is the only group or if clicking same group
        // Just switch to the clicked group
        this.selectedGroupKey = groupKey;
        this.selectedGroup = this.getGroup(this.selectedCard.info.slug, groupKey);

        if (this.selectedGroup) {
            this.selectedGroupItems = this.getValidItems(this.selectedGroup.items);
            this.selectedGroupSubgroups = this.getSubGroups(this.selectedCard.info.slug, groupKey);
        }
    }

    private clearGroupSelection(): void {
        this.selectedGroupKey = '';
        this.selectedGroup = null;
        this.selectedCardGroupKeys = [];
        this.selectedGroupItems = [];
        this.selectedGroupSubgroups = [];
    }

    private getGroupKeys(cardSlug: string): string[] {
        if (!this.cerere.extra_info?.[cardSlug]) return [];

        return Object.keys(this.cerere.extra_info[cardSlug]).filter(key => key !== 'info');
    }

    private getGroup(cardSlug: string, groupKey: string): GroupWithItems | null {
        const group = this.cerere.extra_info?.[cardSlug]?.[groupKey];
        // A group is valid if it has either items OR subgroups (or both)
        return (group && ('items' in group || 'subgroups' in group)) ? group as GroupWithItems : null;
    }

    getGroupDisplayName(groupKey: string): string {
        if (!this.selectedCard) return groupKey;
        const group = this.getGroup(this.selectedCard.info.slug, groupKey);
        return group?.info.name || groupKey;
    }

    private getValidItems(items: FormItem[] | undefined): FormItem[] {
        if (!items) return [];
        return items.filter(item => item.value !== null && item.value !== '-1');
    }

    private getValidRow(row: FormItem[] | undefined): FormItem[] {
        if (!row) return [];
        return row.filter(item => item.value !== null && item.value !== '-1');
    }

    private getSubGroups(cardSlug: string, groupKey: string): SubGroup[] {
        const group = this.getGroup(cardSlug, groupKey);
        if (!group?.subgroups) return [];

        return Object.values(group.subgroups).map((subgroup: SubGroup) => ({
            info: subgroup.info,
            rows: subgroup.rows ? subgroup.rows
                .map(row => this.getValidRow(row)) // filter invalid items per row
                .filter(row => row.length > 0)     // drop empty rows entirely
                : undefined,
            items: subgroup.items ? this.getValidItems(subgroup.items) : undefined,
        }));
    }

    // TrackBy functions for performance
    trackByCardId(index: number, card: CardWithForms): number {
        return card.info.id;
    }

    trackByGroupKey(index: number, groupKey: string): string {
        return groupKey;
    }

    trackByItemId(index: number, item: FormItem): number {
        return item.id;
    }

    trackBySubgroupId(index: number, subgroup: SubGroup): number {
        return subgroup.info.id;
    }

    trackByRowIndex(index: number): number {
        return index;
    }
}
